import { config } from "@/config";
import { getCurrentLocale } from "@/i18n/locales";
import { readStorage, writeStorage, removeStorage } from "@/utils/storage";
import { decodeSaveArchives, decodeSaveStatus } from "./save-decoder";
import type {
  Allocation, QueueTicket, StarparksLaunchConfiguration, StarparksSession,
} from "./types";

interface Envelope<T> { code: number; msg?: string; detail?: string; data: T }

function metadataHeaders(session: StarparksSession): Headers {
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json;charset=utf-8",
    "App-Id": "com.cga.cloudgame.platform",
    "App-Channel": "CHANNEL_ENGLISH_ONLINE",
    "Device-Id": getDeviceId(),
    "Device-TYPE": "android",
    "Version-Code": "260805002",
    "Device-Info": "imei=&model=web&oaid=",
    Country: detectCountry(),
    Language: getCurrentLocale(),
    Authorization: session.sessionToken,
  });
  return headers;
}

async function request<T>(
  session: StarparksSession,
  path: string,
  init: RequestInit = {},
  acceptedCodes: number[] = [0],
): Promise<T> {
  const headers = metadataHeaders(session);
  new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  const response = await fetch(`${config.starparksApiBaseUrl}${path}`, {
    ...init,
    headers,
  });
  let envelope: Envelope<T> | undefined;
  try { envelope = await response.json() as Envelope<T>; } catch { /* handled below */ }
  if (!response.ok || !envelope || !acceptedCodes.includes(envelope.code)) {
    throw new Error(envelope?.detail || envelope?.msg || `Cloud service request failed (${response.status})`);
  }
  return envelope.data;
}

export function getDeviceId(): string {
  const key = "gameglee.web.device";
  let value = readStorage(key);
  if (!value) {
    value = crypto.randomUUID();
    writeStorage(key, value);
  }
  return value;
}

export function resetDeviceId(): void {
  removeStorage("gameglee.web.device");
}

export function detectCountry(): string {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale;
  const region = locale.match(/-([A-Z]{2})\b/)?.[1];
  return region ?? "US";
}

export const starparksApi = {
  launchConfig: (session: StarparksSession, gameId: number) =>
    request<StarparksLaunchConfiguration>(session, `/v1/client/games/${gameId}`),
  ensureSaveReady: (session: StarparksSession, gameId: number) =>
    request<unknown>(session, `/v1/client/profiles/${gameId}/status`, {}, [0, 201017, 201019, 201020]),
  queue: (session: StarparksSession, gameId: number) => request<QueueTicket>(session, "/v1/client/server/queue", {
    method: "POST",
    body: JSON.stringify({
      game_id: gameId,
      version: "2.0",
      room_id: "",
      ...(session.memberQueueEligible ? { fast: { fast_type: 8, product_id: 0, num: 0 } } : {}),
    }),
  }),
  queueStatus: (session: StarparksSession, gameId: number, roomId: string) => {
    const query = new URLSearchParams({ game_id: String(gameId), room_id: roomId, version: "2.0" });
    return request<QueueTicket>(session, `/v1/client/server/queue?${query}`);
  },
  cancelQueue: async (session: StarparksSession, roomId: string, deviceId = getDeviceId()) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    try {
      await request<void>(session, "/v1/client/server/cancelqueue", {
        method: "POST", body: JSON.stringify({ room_id: roomId, version: "2.0" }),
        headers: { "Device-Id": deviceId }, signal: controller.signal, keepalive: true,
      });
    } finally { clearTimeout(timeout); }
  },
  allocate: (session: StarparksSession, gameId: number, gameMode: number) => request<Allocation>(session, "/v1/client/server/allocgp", {
    method: "POST", body: JSON.stringify({ room_id: 0, game_id: gameId, game_mode: gameMode, version: "2.0" }),
  }),
  saveStatus: (session: StarparksSession, gameId: number) =>
    request<unknown>(session, `/v1/client/profiles/${gameId}/status`, {}, [0, 201017, 201019, 201020]).then(decodeSaveStatus),
  saveArchives: (session: StarparksSession, gameId: number) =>
    request<unknown>(session, `/v1/client/profiles/${gameId}/tag`).then(decodeSaveArchives),
  renameSave: (session: StarparksSession, saveId: number, remark: string) =>
    request<unknown>(session, `/v1/client/profiles/${saveId}/remark`, {
      method: "POST", body: JSON.stringify({ remark: remark.trim() }),
    }),
  restoreSave: (session: StarparksSession, saveId: number, gameId: number) =>
    request<unknown>(session, `/v1/client/profiles/${saveId}`, {
      method: "POST", body: JSON.stringify({ game_id: gameId }),
    }),
};
